/*
 * Caller ID numbers store - for MyPresence
 */

import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useApiStore } from '@/stores/Api'
import { useRealtimeStore } from '@/stores/Realtime'
import { useEmployeesStore } from '@/stores/Employees'
import _i18n from '@/utilities/I18n'

import logger from '@/utilities/Logger'
import globalEvents from '@/utilities/GlobalEvents'

import type { PbxExtensionCallerIdNumbersResponse } from '@/TcxApi/PbxExtension'
import type Employee from './Employee'
import { sleep } from '@/utilities/Tools'

export const useCallerIdNumbersStore = defineStore('callerIdNumbers', () => {
	logger.debug('CallerIdNumbers: Initializing store')

	const ss = useSettingsStore()
	const api = useApiStore()
	const realtime = useRealtimeStore()
	const employees = useEmployeesStore()
	const i18n = _i18n.global

	const numbers = ref<{ number: string, name: string }[]>([])

	init()

	async function init() {
		await sleep(1)
		await ss.ready()

		globalEvents.on('Auth:LoggedIn', loadNumbers)
		realtime.configEvents.on('EMPLOYEE_UPDATED', async item => {
			if (item.id === ss.auth?.employee) {
				await loadNumbers()
			}
		})

		realtime.configEvents.on('PBX_TRUNK_CREATED', loadNumbers)
		realtime.configEvents.on('PBX_TRUNK_DELETED', loadNumbers)
		realtime.configEvents.on('PBX_TRUNK_UPDATED', loadNumbers)
	}

	async function loadNumbers() {
		try {
			await employees.ready()

			const res2 = await api.get<PbxExtensionCallerIdNumbersResponse>('/pbx/extension/calleridnumbers?v2=true')

			res2.data.sort((a, b) => Number(a.number.substring(1)) - Number(b.number.substring(1)))
			numbers.value = res2.data

			const me = employees.me as Employee

			if (me.primaryExtension) {
				if (me.primaryExtension.mobile.length > 0 || me.primaryExtension?.direct) {
					numbers.value.unshift({ number: '---', name: '' })
				}

				if (me.primaryExtension.mobile.length > 0) {
					for (const number of me.primaryExtension.mobile) {
						numbers.value.unshift({ number: number, name: i18n.t('common.mobile') })
					}
				}
				if (me.primaryExtension.direct) {
					numbers.value.unshift({ number: me.primaryExtension.direct, name: i18n.t('common.direct') })
				}
			}

			logger.debug(`CallerIdNumbers: Loaded ${numbers.value.length} numbers`)
		}
		catch (err) {
			logger.error('CallerIdNumbers: Failed to load numbers: ' + (err as Error).message)
			numbers.value = []
		}
	}

	return { numbers }
})
